import { useState, useMemo, useRef, useEffect, useCallback } from 'react'
import { usePlayground } from '../store'
import { type StratumChangeInfo } from './StratumNavigator'
import { Workspace } from './Workspace'
import { titleCase } from '../utils/titleCase'
import { TabToolbar, ToolbarBtn, ProcessingDot, Icons } from './TabToolbar'

export function Sidebar() {
    const result = usePlayground((s) => s.result)
    const scene = usePlayground((s) => s.scene)
    const parsedStatements = usePlayground((s) => s.parsedStatements)
    const selectedStatementIndex = usePlayground((s) => s.selectedStatementIndex)
    const focusedNodeId = usePlayground((s) => s.focusedNodeId)
    const selectNode = usePlayground((s) => s.selectNode)
    const dismissEntity = usePlayground((s) => s.dismissEntity)
    const isMobile = usePlayground((s) => s.isMobile)
    const rightOpen = usePlayground((s) => s.rightOpen)
    const setRightOpen = usePlayground((s) => s.setRightOpen)
    const projectName = usePlayground((s) => s.projectName)
    const isProcessing = usePlayground((s) => s.isProcessing)

    // Navigation — from store
    const focusStack = usePlayground((s) => s.focusStack)
    const pushFocus = usePlayground((s) => s.pushFocus)
    const popFocus = usePlayground((s) => s.popFocus)

    const [, setStratumInfo] = useState<StratumChangeInfo | null>(null)

    // Track nav depth for history API
    const navDepthRef = useRef(0)
    const suppressPopRef = useRef(false)

    const canGoBack = focusStack.length > 0 || focusedNodeId !== null

    const handleExport = useCallback(() => {
        if (!result) return
        const data = {
            actionCount: result.actionCount,
            entityCount: result.entityCount,
            nodeCount: result.nodeCount,
            edgeCount: result.edgeCount,
        }
        const json = JSON.stringify(data, null, 2)
        const blob = new Blob([json], { type: 'application/json' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `${(projectName || 'system').replace(/\s+/g, '-').toLowerCase()}-analysis.json`
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)
    }, [result, projectName])

    const handleBack = useCallback(() => {
        if (focusedNodeId) {
            dismissEntity()
        } else if (focusStack.length > 0) {
            popFocus()
            dismissEntity()
        }
    }, [focusedNodeId, focusStack.length, dismissEntity, popFocus])

    // Push browser history entry when navigating deeper
    const handlePushFocus = useCallback((id: string) => {
        pushFocus(id)
        navDepthRef.current++
        history.pushState({ enNavDepth: navDepthRef.current }, '')
    }, [pushFocus])

    // Also push history when a node is focused (via node click)
    const prevFocusedRef = useRef<string | null>(null)
    useEffect(() => {
        if (focusedNodeId && focusedNodeId !== prevFocusedRef.current) {
            navDepthRef.current++
            history.pushState({ enNavDepth: navDepthRef.current }, '')
        }
        prevFocusedRef.current = focusedNodeId
    }, [focusedNodeId])

    // Handle Android/browser back button via popstate
    useEffect(() => {
        const onPopState = () => {
            if (suppressPopRef.current) {
                suppressPopRef.current = false
                return
            }
            if (navDepthRef.current > 0) {
                navDepthRef.current--
                handleBack()
            }
        }
        window.addEventListener('popstate', onPopState)
        return () => window.removeEventListener('popstate', onPopState)
    }, [handleBack])

    // Breadcrumb logic
    const breadcrumbs = useMemo(() => {
        const list: { label: string; id: string; type: string }[] = [
            { label: projectName || 'System', id: 'root', type: 'system' }
        ]

        if (result?.stratumHierarchy) {
            focusStack.forEach(id => {
                const s = result.stratumHierarchy!.strata[id]
                if (s) list.push({ label: s.name, id: s.id, type: 'stratum' })
            })
        }

        if (focusedNodeId && scene) {
            const node = scene.nodes.find(n => n.id === focusedNodeId)
            if (node) list.push({ label: node.displayName, id: node.id, type: 'node' })
        }
        return list
    }, [projectName, focusStack, focusedNodeId, scene, result?.stratumHierarchy])

    const breadcrumbsRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (breadcrumbsRef.current) {
            breadcrumbsRef.current.scrollLeft = breadcrumbsRef.current.scrollWidth
        }
    }, [breadcrumbs])

    const handleBackClick = () => {
        if (navDepthRef.current > 0) {
            history.back()
        } else {
            handleBack()
        }
    }

    const handleNodeSelect = useCallback((id: string) => selectNode(id, false), [selectNode])

    const showToolbar = true
    const showContent = true

    return (
        <div className="flex flex-col h-full overflow-hidden">
            {showToolbar && <TabToolbar
                left={<>
                    {canGoBack ? (
                        <ToolbarBtn onClick={handleBackClick} title="Back">{Icons.back}</ToolbarBtn>
                    ) : (
                        <div className="w-8 h-8 flex items-center justify-center" style={{ color: 'var(--text-muted)' }}>{Icons.tabStructure}</div>
                    )}
                    <span className="text-xs font-bold font-mono uppercase tracking-widest" style={{ color: 'var(--text-muted)' }}>
                        {breadcrumbs.length > 1 ? titleCase(breadcrumbs[breadcrumbs.length - 1].label) : 'Structure'}
                    </span>
                    {isProcessing && <ProcessingDot />}
                </>}
                right={<>
                    {result && <ToolbarBtn onClick={handleExport} title="Export JSON">{Icons.download}</ToolbarBtn>}
                    {!isMobile && <ToolbarBtn onClick={() => setRightOpen(false)} title="Close panel">{Icons.close}</ToolbarBtn>}
                </>}
            />}

            {/* Content Area — hidden when collapsed on desktop */}
            {showContent && (isMobile || rightOpen) && <div className="flex-1 overflow-y-auto">
                <Workspace
                    result={result}
                    scene={scene}
                    parsedStatements={parsedStatements}
                    focusedNodeId={focusedNodeId}
                    focusedStatement={selectedStatementIndex !== null ? parsedStatements[selectedStatementIndex] : null}
                    onNodeSelect={handleNodeSelect}
                    onStratumChange={setStratumInfo}
                    focusStack={focusStack}
                    onPushFocus={handlePushFocus}
                />
            </div>}
        </div>
    )
}

